import {
  AnswerOptionVariableDefTypeEnum,
  MasterFieldDef,
  MasterFieldTypeEnum,
  ResponseDatetimeSubTypeEnum,
  ResponseFormulaTokenTypeEnum,
  ResponseTypeEnum,
  TemplateLayoutTypeEnum,
  TemplateNodeSchema,
  TemplateNodeTypeEnum,
  TemplatePageSchema,
} from '@ulysses-inc/harami_api_client'
import { TemplateNodesDict, TemplatePagesDict } from 'src/exShared/types/types'
import { isSection } from 'src/exShared/util/nodes/isSection'
import {
  buildSiblingQuestionMapByUuid,
  findSiblingNodeIds,
} from 'src/features/templateEdit/common/siblingNodes'
import { isNullish } from 'src/util/isNullish'
import { ValidationErrorType } from '../../firebase/featureSpecificEventLog/gridLayoutSoftLimitError'
import { validate as validateTimeMeasurementRule } from '../editResponseTimeMeasurementRule/validateMeasurementRule'

// 表形式で登録できる最大のページ数
const MAX_GRID_PAGE = 70
// 表形式で登録できるページ単位の最大質問、条件分岐数
const MAX_GRID_PAGE_QUESTION_AND_LOGIC = 50
// 表形式のひな形 条件分岐最大ネスト数
const MAX_GRID_LOGIC_NEST_COUNT = 3
// ソフトリミットを拡張する特定企業ID
const SOFT_LIMIT_SPECIAL_HANDLING_COMPANY_ID = '754'
const MAX_GRID_PAGE_QUESTION_AND_LOGIC_FOR_SPECIAL_HANDLING = 360

// 渡された nodeIds の node 及び、その子 node に質問があるかを返す
const hasQuestionNode = (
  templateNodes: TemplateNodesDict,
  nodeIds: number[],
): boolean => {
  // nodeIds の node 及び、その子 node に一つでも質問があれば true を返す
  return nodeIds.some(id => {
    const node = templateNodes[id]
    if (!node) {
      return false
    }
    if (node.type === TemplateNodeTypeEnum.Question) {
      return true
    }
    if (node.nodes.length === 0) {
      return false
    }

    // node に子 node がある場合は、再起的に呼び出す
    return hasQuestionNode(templateNodes, node.nodes)
  })
}

// page に少なくとも一つの質問が紐づいているかのチェック
export const validatePageHasQuestion = (
  templateNodes: TemplateNodesDict,
  templatePages: TemplatePagesDict,
): boolean => {
  // 1 page ずつ確認して、全ての page が少なくとも一つの質問を持っていることを確認している
  return Object.keys(templatePages).every(pageId => {
    // page 直下の node の id 配列を取得
    const nodeIdsDividedByPage = templatePages[Number(pageId)]?.nodes
    if (!nodeIdsDividedByPage) {
      return false
    }
    return hasQuestionNode(templateNodes, nodeIdsDividedByPage)
  })
}

/**
 * 渡された nodeIds の node 及び、その子 node に質問・条件分岐がある場合、質問数を加算して返す
 *
 * @param templateNodes
 * @param nodeIds
 * @param tmpQuestionAndLogicCount
 * @returns
 */
const getQuestionAndLogicNodeCount = (
  templateNodes: TemplateNodesDict,
  nodeIds: number[],
  tmpQuestionAndLogicCount: number,
): number => {
  // nodeIds の node 及び、その子 node に質問があればカウントを加算し、質問数を返す
  let currentQuestionAndLogicCount = tmpQuestionAndLogicCount

  for (const nodeId of nodeIds) {
    const node = templateNodes[nodeId]
    if (node) {
      if (
        node.type === TemplateNodeTypeEnum.Question ||
        node.type === TemplateNodeTypeEnum.Logic
      ) {
        currentQuestionAndLogicCount += 1
      }
      // node に子 node がある場合は、再起的に呼び出す
      currentQuestionAndLogicCount = getQuestionAndLogicNodeCount(
        templateNodes,
        node.nodes,
        currentQuestionAndLogicCount,
      )
    }
  }
  return currentQuestionAndLogicCount
}

/**
 * page 紐づく質問数+条件分岐数が最大数を超過していないかのチェック
 * 最大数を超過した場合、 false を返す
 * 質問がない、もしくは最大数を超過していない場合、 true を返す
 *
 * @param templateNodes
 * @param templatePages
 * @returns
 */
export const validatePageQuestionAndLogicCount = (
  templateNodes: TemplateNodesDict,
  templatePages: TemplatePagesDict,
  companyId: string,
): boolean => {
  // 1 page ずつ確認して、全ての page で質問数が最大数を超過していないことを確認する
  return Object.keys(templatePages).every(pageId => {
    // page 直下の node の id 配列を取得
    const nodeIdsDividedByPage = templatePages[Number(pageId)]?.nodes
    // 質問がなければ
    if (!nodeIdsDividedByPage) {
      return true
    }
    // 特定顧客の場合はソフトリミットを拡張する
    // 経緯: https://kaminashi.atlassian.net/browse/INE-1904
    if (companyId === SOFT_LIMIT_SPECIAL_HANDLING_COMPANY_ID) {
      return (
        MAX_GRID_PAGE_QUESTION_AND_LOGIC_FOR_SPECIAL_HANDLING >=
        getQuestionAndLogicNodeCount(templateNodes, nodeIdsDividedByPage, 0)
      )
    } else {
      return (
        MAX_GRID_PAGE_QUESTION_AND_LOGIC >=
        getQuestionAndLogicNodeCount(templateNodes, nodeIdsDividedByPage, 0)
      )
    }
  })
}

// pages から、取り込み項目詳細が登録済みかどうかをチェックする
// 1ページでも登録済みの場合、 false を返す
// それ以外の場合、 true を返す
export const validateHasRegisteredVariables = (
  templateNodes: TemplateNodesDict,
  templatePages: TemplatePagesDict,
): boolean => {
  for (const pageId in templatePages) {
    const nodeIds = templatePages[pageId]?.nodes
    // `nodeIds` の中に「セクション」なノードが含まれているので、それを探す
    if (!nodeIds) {
      continue
    }
    const sectionId = nodeIds.find(nodeId => {
      const maybeSectionNode = templateNodes[nodeId]
      return maybeSectionNode !== undefined && isSection(maybeSectionNode)
    })

    if (
      sectionId !== undefined &&
      templateNodes[sectionId]?.section?.hasRegisteredVariables
    ) {
      return false
    }
  }
  return true
}

/**
 * 引数で受け取った時間計測質問が参照している日時質問が、仕様的に正しいかどうかをチェックする
 *
 * 仕様より参照先は同一の親(セクション・条件分岐・ページ)に属する日時質問のみなので、
 * 計算式やラベル検査と同様に、兄弟の中から参照先を引くことで同一階層であることも担保する
 *
 * @param tmNode 時間計測質問のノード
 * @param context ノード・ページの一覧
 * @returns エラーメッセージ(エラーなしなら空文字)
 */
export const validateTimeMeasurement = (
  tmNode: TemplateNodeSchema,
  context: {
    templatePages: TemplatePageSchema[]
    templateNodes: TemplateNodeSchema[]
    templateNodesDict: TemplateNodesDict
  },
) => {
  const { templatePages, templateNodes, templateNodesDict } = context
  const tmQuestionName = tmNode.question?.name || ''
  const tm = tmNode.question?.responseTimeMeasurements?.[0]

  if (isNullish(tm)) {
    return `質問名：「${tmQuestionName}」の計測が未設定です`
  }

  const siblingNodeIds = findSiblingNodeIds(
    tmNode.id,
    templateNodes,
    templatePages,
  )
  // ページ・ページ以外のいずれの親も見つからない時はおそらくデータが壊れている
  if (isNullish(siblingNodeIds)) {
    return `質問名：「${tmQuestionName}」の計測設定が不正です`
  }
  const siblingDatetimeNodeMapByUUID = buildSiblingQuestionMapByUuid(
    siblingNodeIds,
    templateNodesDict,
    ResponseTypeEnum.DATETIME,
  )

  const { startQuestionNodeUUID, endQuestionNodeUUID } = tm

  // 参照先の日時質問が同一階層に存在するかどうかをチェックする
  const start = siblingDatetimeNodeMapByUUID.get(startQuestionNodeUUID)
    ?.question?.responseDatetimes?.[0]

  if (isNullish(start)) {
    return `質問名：「${tmQuestionName}」の「開始時間の質問」を選択してください`
  }
  if (start.subType === ResponseDatetimeSubTypeEnum.DATE) {
    return `質問名：「${tmQuestionName}」の「開始時間の質問」のフォーマットを「日時」または「時刻」に変更してください`
  }

  const end =
    siblingDatetimeNodeMapByUUID.get(endQuestionNodeUUID)?.question
      ?.responseDatetimes?.[0]
  if (isNullish(end)) {
    return `質問名：「${tmQuestionName}」の「終了時間の質問」を選択してください`
  }
  if (end.subType === ResponseDatetimeSubTypeEnum.DATE) {
    return `質問名：「${tmQuestionName}」の「終了時間の質問」のフォーマットを「日時」または「時刻」に変更してください`
  }

  // 参照先の日時質問のフォーマットをチェックし、同じフォーマットかどうかをチェックする
  if (start.subType !== end.subType) {
    return `質問名：「${tmQuestionName}」の「開始時間の質問」と「終了時間の質問」に、同一フォーマット(日時または時刻)の質問を選択してください`
  }

  // ルール設定のvalidationチェック
  const tmr = tm.rule
  const errRuleMsg = validateTimeMeasurementRule(
    !!tmr,
    start.subType,
    tmr?.type,
    tmr?.value ?? 0,
  )
  if (errRuleMsg !== '') {
    return `質問名：「${tmQuestionName}」のルール設定で、${errRuleMsg}`
  }

  return ''
}

/**
 * page 配下の validate
 * 現状ではエラーメッセージを一つしか設定できないので一つでもエラーになったらその時点で return する
 *
 * @param templateNodesDict
 * @param templatePagesDict
 * @param companyId
 * @returns
 */
export const validateGridTemplatePage = (
  templateNodesDict: TemplateNodesDict,
  templatePagesDict: TemplatePagesDict,
  companyId: string,
): [boolean, string, ValidationErrorType?] => {
  const templateNodes = Object.values(templateNodesDict)
  const templatePages = Object.values(templatePagesDict)

  if (Object.keys(templatePagesDict).length === 0) {
    return [false, '少なくとも1つページを設定してください']
  }

  if (!validatePageHasQuestion(templateNodesDict, templatePagesDict)) {
    return [false, '少なくとも1つ質問を設定してください']
  }

  const questionNodes = Object.values(templateNodesDict).filter(
    node => node.type === TemplateNodeTypeEnum.Question,
  )
  let questionErrorMessage = ''
  const isInvalidQuestion = questionNodes.some(node => {
    // 表形式のひな形における以下チケットの事象に対応するため、必須かどうか問わず回答項目未選択の場合入力不可能とした
    // https://kaminashi.atlassian.net/browse/HPB-3172
    // 表形式以外も上記仕様に {@link https://kaminashi.atlassian.net/browse/INE-1222}
    const isUnselectedResponseType = !node.question?.responseType
    if (isUnselectedResponseType) {
      questionErrorMessage = `質問名：「${node.question?.name}」の回答項目が設定されていません`
      return true
    }

    // APIサーバ側の handler でのリクエストボディのバリデーションでは、コードポイントで数えているのでそれに合わせている。
    // そのため「node.question.name.length」を使っていない。
    if (node.question?.name && Array.from(node.question.name).length > 255) {
      questionErrorMessage = '質問名の長さは最大でも255文字でなければなりません'
      return true
    }

    // ***以下で各回答種別で特有のバリデーションを行う***

    // *時間計測質問のバリデーション
    if (node.question?.responseType === ResponseTypeEnum.TIME_MEASUREMENT) {
      // この時間計測質問(tm)が参照している質問が、仕様的に正しい日時質問かどうかをチェックする
      const errMsg = validateTimeMeasurement(node, {
        templatePages,
        templateNodes,
        templateNodesDict,
      })

      if (errMsg !== '') {
        questionErrorMessage = errMsg
        return true
      }
    }

    // 計算式質問のバリデーション
    if (node.question?.responseType === ResponseTypeEnum.FORMULA) {
      const errMsg = validateFormula(node, {
        templatePages,
        templateNodes,
        templateNodesDict: templateNodesDict,
      })

      if (errMsg !== '') {
        questionErrorMessage = errMsg
        return true
      }
    }

    return false
  })
  if (isInvalidQuestion) {
    return [false, questionErrorMessage]
  }

  const sectionNodes = Object.values(templateNodesDict).filter(node => {
    return node.type === TemplateNodeTypeEnum.Section
  })

  let sectionErrorMessage = ''
  const isInvalidSection = sectionNodes.some(node => {
    // セクション名は必須: 半角/全角スペースのみの場合もエラーとする
    if (!node.section?.name || node.section.name.trim() === '') {
      sectionErrorMessage = 'セクション名を入力してください'
      return true
    }

    // APIサーバ側の handler でのリクエストボディのバリデーションでは、コードポイントで数えているのでそれに合わせている。
    // そのため「node.question.name.length」を使っていない。
    if (node.section?.name && Array.from(node.section.name).length > 255) {
      sectionErrorMessage =
        'セクション名の長さは最大でも255文字でなければなりません'
      return true
    }

    return false
  })
  if (isInvalidSection) {
    return [false, sectionErrorMessage]
  }

  // ひな形内でセクション名は重複できない
  // セクション名の必須チェックの後に実行されることを想定している
  const set = new Set<string>()
  sectionNodes.forEach(sectionNode => {
    if (sectionNode.section?.name) {
      set.add(sectionNode.section?.name)
    }
  })
  if (set.size !== sectionNodes.length) {
    return [false, 'セクション名は重複できません']
  }

  // 1 ページの合計質問数+条件分岐数が MAX_GRID_PAGE_QUESTION 以上の場合、エラーとする
  // NOTE: 表形式では ページとセクションはほぼ同義となる
  // そのため、1ページに複数セクション登録できるような仕様変更が行われた場合
  // 該当処理の見直しが必要
  if (
    !validatePageQuestionAndLogicCount(
      templateNodesDict,
      templatePagesDict,
      companyId,
    )
  ) {
    return [
      false,
      `質問数、条件分岐数の合計が${MAX_GRID_PAGE_QUESTION_AND_LOGIC}個を超えたため、保存できません。質問または条件分岐を減らしてください。`,
      'pageQuestionAndLogicCount',
    ]
  }

  // ページ数が MAX_GRID_PAGE 以上の場合、エラーとする
  // NOTE: 表形式では ページとセクションはほぼ同義となる
  // そのため、1ページに複数セクション登録できるような仕様変更が行われた場合
  // 該当処理の見直しが必要
  if (Object.keys(templatePagesDict).length > MAX_GRID_PAGE) {
    return [
      false,
      `セクション数が${MAX_GRID_PAGE}個を超えたため、保存できません。ページを減らしてください。`,
      'pageCount',
    ]
  }

  return [true, '']
}

/**
 * 指定された Question node で分岐を追加しようとした時に、
 * 分岐のネスト数がソフトリミットを超過していないかを検証する。
 *
 * @param layoutType 行形式のひな形の場合、ソフトリミットの制約をかけないために指定
 * @param clickedQuestionNode
 * @param allNodes
 * @returns ソフトリミットを超過している場合、エラーメッセージが返る。超過していない場合は、空文字が返る
 */
export const validateLogicNestCountOnInsertingNewLogic = (
  layoutType: TemplateLayoutTypeEnum,
  clickedQuestionNode: TemplateNodeSchema,
  allNodes: TemplateNodesDict,
): string => {
  if (layoutType !== TemplateLayoutTypeEnum.Grid) {
    return ''
  }
  return validateLogicCount(
    // 条件分岐を追加するアイコンをクリックした質問までの条件分岐に、1つ追加したら条件を超過する場合、エラーとなる、という意味
    countCurrentAndParentLogicNode(clickedQuestionNode, allNodes) + 1,
  )
}

/**
 * Question node が Drag & Drop されようとした時に、
 * Drop 先で、条件分岐のネスト数が、ソフトリミットを超過していないかを検証する
 *
 * @param layoutType 行形式のひな形の場合、ソフトリミットの制約をかけないために指定
 * @param draggedQuestionNode Drag されている質問
 * @param dropParentNode Drop 先の親ノード
 * @param allNodes
 * @returns
 */
export const validateLogicNestCountOnDraggingQuestion = (
  layoutType: TemplateLayoutTypeEnum,
  draggedQuestionNode: TemplateNodeSchema,
  dropParentNode: TemplateNodeSchema | null,
  allNodes: TemplateNodesDict,
): string => {
  if (layoutType !== TemplateLayoutTypeEnum.Grid) {
    return ''
  }
  return validateLogicCount(
    (dropParentNode
      ? countCurrentAndParentLogicNode(dropParentNode, allNodes)
      : 0) +
      workChildrenAndGetMaxLogicNestCount(draggedQuestionNode, allNodes, 0),
  )
}

const validateLogicCount = (logicCount: number) => {
  return logicCount <= MAX_GRID_LOGIC_NEST_COUNT
    ? ''
    : `条件分岐は${
        MAX_GRID_LOGIC_NEST_COUNT + 1
      }階層以上設定することができません。`
}

/**
 * 自分自身も含め、子ノード内で、最大の条件分岐のネスト数を取得する
 *
 * @param currentNode
 * @param allNodes
 * @param logicCountToParentNode
 * @returns
 */
const workChildrenAndGetMaxLogicNestCount = (
  currentNode: TemplateNodeSchema,
  allNodes: TemplateNodesDict,
  logicCountToParentNode: number,
) => {
  const logicCountToThisNode =
    currentNode.type === TemplateNodeTypeEnum.Logic
      ? logicCountToParentNode + 1
      : logicCountToParentNode

  let maxLogicCount = logicCountToThisNode
  for (const nodeId of currentNode.nodes) {
    const child = allNodes[nodeId]
    if (!child) {
      continue
    }
    const tempMax = workChildrenAndGetMaxLogicNestCount(
      child,
      allNodes,
      logicCountToThisNode,
    )

    maxLogicCount = Math.max(maxLogicCount, tempMax)
  }

  return maxLogicCount
}

/**
 * 自分自身も含め、自分自身から、最上位の親ノードまでの間にある、条件分岐数をカウントする
 *
 * @param currentNode
 * @param allNodes
 * @returns
 */
const countCurrentAndParentLogicNode = (
  currentNode: TemplateNodeSchema,
  allNodes: TemplateNodesDict,
): number => {
  const parentIdByNodeId: { [key: number]: number } = {}
  Object.values(allNodes).forEach(node => {
    node.nodes.forEach(nodeId => (parentIdByNodeId[nodeId] = node.id))
  })

  let currentNodeId: number | undefined = currentNode.id

  let parentLogicCount = 0
  while (currentNodeId) {
    const tempNode: TemplateNodeSchema | undefined = allNodes[currentNodeId]
    if (!tempNode) {
      break
    }
    if (tempNode.type === TemplateNodeTypeEnum.Logic) {
      parentLogicCount++
    }

    currentNodeId = parentIdByNodeId[tempNode.id]
  }

  return parentLogicCount
}

/**
 * 製品マスタから自動入力の設定のバリデーション.
 *
 * - テキスト質問：文字列型の項目が選択がされていない場合、エラーメッセージを返す
 * - 数値質問：数値型の項目が選択がされていない場合、エラーメッセージを返す
 *
 * @param templateNodes
 * @param masterFieldDefs
 * @returns
 */
export const validateQuestionAnswerOptions = (
  templateNodes: TemplateNodeSchema[],
  masterFieldDefs: MasterFieldDef[],
): string => {
  const numberMasterFieldDefIds = new Set()
  const textMasterFieldDefIds = new Set()

  for (const masterFieldDef of masterFieldDefs) {
    if (masterFieldDef.fieldType === MasterFieldTypeEnum.Number) {
      numberMasterFieldDefIds.add(masterFieldDef.id)
    }
    if (masterFieldDef.fieldType === MasterFieldTypeEnum.Text) {
      textMasterFieldDefIds.add(masterFieldDef.id)
    }
  }

  for (const node of templateNodes) {
    if (
      node.type !== TemplateNodeTypeEnum.Question ||
      !node.question?.answerOption
    ) {
      continue
    }

    const answerOption = node.question.answerOption
    const masterFieldDefId = answerOption.itemVariableDef?.masterFieldDefId ?? 0

    if (node.question.responseType === ResponseTypeEnum.NUMBER) {
      if (
        answerOption.variableDefType !== AnswerOptionVariableDefTypeEnum.Item ||
        !numberMasterFieldDefIds.has(masterFieldDefId)
      ) {
        return `質問名：「${node.question.name}」の製品マスタから自動入力には、数値型の項目を設定してください`
      }
    }

    if (node.question.responseType === ResponseTypeEnum.TEXT) {
      if (
        answerOption.variableDefType === AnswerOptionVariableDefTypeEnum.Item &&
        !textMasterFieldDefIds.has(masterFieldDefId)
      ) {
        return `質問名：「${node.question.name}」の製品マスタから自動入力には、テキスト型の項目を設定してください`
      }
    }
  }

  return ''
}

/**
 * 計算式質問のバリデーション
 *
 * @param targetNode
 * @param context
 * @returns
 */
export const validateFormula = (
  targetNode: TemplateNodeSchema,
  context: {
    templatePages: TemplatePageSchema[]
    templateNodes: TemplateNodeSchema[]
    templateNodesDict: TemplateNodesDict
  },
): string => {
  if (targetNode.question?.responseType !== ResponseTypeEnum.FORMULA) {
    return ''
  }
  const formulaNode = targetNode

  const { templatePages, templateNodes, templateNodesDict } = context
  const responseFormula = formulaNode.question?.responseFormulas?.[0]

  if (
    !responseFormula ||
    !responseFormula.tokens ||
    responseFormula.tokens.length === 0
  ) {
    return `質問名：「${formulaNode.question?.name}」の計算式が未設定です`
  }

  if (responseFormula.tokens.length < 3) {
    return `質問名：「${formulaNode.question?.name}」の計算式が不正です`
  }

  const siblingNodeIds = findSiblingNodeIds(
    formulaNode.id,
    templateNodes,
    templatePages,
  )
  // ページ・ページ以外のいずれの親も見つからない時はおそらくデータが壊れている
  if (!siblingNodeIds) {
    return `質問名：「${formulaNode.question?.name}」の計算式が不正です`
  }
  const siblingNumberNodeMapByUUID = buildSiblingQuestionMapByUuid(
    siblingNodeIds,
    templateNodesDict,
    ResponseTypeEnum.NUMBER,
  )

  let editableQuestionCount = 0
  for (const token of responseFormula.tokens) {
    if (token.type !== ResponseFormulaTokenTypeEnum.QUESTION) {
      continue
    }
    const questionNode = siblingNumberNodeMapByUUID.get(
      token.questionNodeUUID ?? '',
    )
    if (!questionNode) {
      return `質問名：「${formulaNode.question?.name}」の計算式が不正です`
    }
    if (!questionNode.question?.answerOption) {
      editableQuestionCount++
    }
  }

  if (editableQuestionCount === 0) {
    return `質問名：「${formulaNode.question?.name}」の計算式には、記録時に入力可能な質問を1つ以上含めてください`
  }
  return ''
}

/**
 * ラベル検査のバリデーション
 *
 * @param targetNode
 * @param context
 * @returns
 */
export const validateLabelCheck = (
  targetNode: TemplateNodeSchema,
  context: {
    templatePages: TemplatePageSchema[]
    templateNodes: TemplateNodeSchema[]
    templateNodesDict: TemplateNodesDict
  },
): string => {
  if (targetNode.question?.responseType !== ResponseTypeEnum.LABEL_CHECK) {
    return ''
  }
  const labelCheckNode = targetNode

  const { templatePages, templateNodes, templateNodesDict } = context

  const responseLabelCheck = labelCheckNode.question?.responseLabelChecks?.[0]

  // responseLabelCheckが存在しない場合はスキップ
  if (!responseLabelCheck) {
    return ''
  }

  const siblingNodeIds = findSiblingNodeIds(
    labelCheckNode.id,
    templateNodes,
    templatePages,
  )
  // ページ・ページ以外のいずれの親も見つからない時はおそらくデータが壊れている
  if (!siblingNodeIds) {
    return `質問名：「${labelCheckNode.question?.name}」の設定が不正です`
  }

  // baseTimeReferenceDatetimeQuestionNodeUUIDのバリデーション
  if (responseLabelCheck.baseTimeReferenceDatetimeQuestionNodeUUID) {
    const siblingDatetimeNodeMapByUUID = buildSiblingQuestionMapByUuid(
      siblingNodeIds,
      templateNodesDict,
      ResponseTypeEnum.DATETIME,
    )

    // 基準日時質問が同じ階層に存在するかチェック
    const baseTimeReferenceQuestion = siblingDatetimeNodeMapByUUID.get(
      responseLabelCheck.baseTimeReferenceDatetimeQuestionNodeUUID,
    )

    if (!baseTimeReferenceQuestion) {
      return `質問名：「${labelCheckNode.question?.name}」が基準にする日時質問が不正です`
    }

    // 基準日時質問のフォーマットが「日時」以外の場合はエラー(フォーマットが「日付」や「時刻」の場合はエラー)
    const baseDatetime =
      baseTimeReferenceQuestion.question?.responseDatetimes?.[0]
    if (baseDatetime?.subType !== ResponseDatetimeSubTypeEnum.DATETIME) {
      return `質問名：「${labelCheckNode.question?.name}」の「日付質問を基準時間にする」に設定されている日時質問のフォーマットを「日時」に変更してください`
    }
  }

  // referenceTextQuestionNodeUUIDのバリデーション
  if (responseLabelCheck.referenceTextQuestionNodeUUID) {
    const siblingTextNodeMapByUUID = buildSiblingQuestionMapByUuid(
      siblingNodeIds,
      templateNodesDict,
      ResponseTypeEnum.TEXT,
    )

    // 参照テキスト質問が同じ階層に存在するかチェック
    const referenceTextQuestion = siblingTextNodeMapByUUID.get(
      responseLabelCheck.referenceTextQuestionNodeUUID,
    )

    if (!referenceTextQuestion) {
      return `質問名：「${labelCheckNode.question?.name}」が参照するテキスト質問が不正です`
    }
  }

  return ''
}
